import React, { useMemo } from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import MuiTypography, { TypographyProps as MuiTypographyProps } from '@material-ui/core/Typography';
import classNames from 'classnames';
import { isNumber, isString } from 'lodash';

import { Link } from './Link';

const styles = ({
  spacing: { unit },
  palette: {
    common: { white },
  },
  custom: {
    palette: {
      colorSupportMidnight,
      colorSupportSteel,
      colorSupportSlate,
      colorAccentTeal,
      colorAccentAloe,
      colorAccentWatermelon,
      colorAccentSunrise,
    },
    fontSizes: { title, heading, subHeading, bodyG, body, bodyS, small, tiny: detail },
  },
}: Theme) =>
  createStyles({
    variantCustomHeader: {
      fontSize: subHeading,
      fontWeight: 900,
      textTransform: 'uppercase',
    },
    variantH4LargeStrongBold: {
      fontWeight: 600,
      fontSize: title,
    },
    variantBody1Large: {
      fontSize: bodyG,
    },
    variantBold: {
      fontWeight: 500,
    },
    variantH1Semibold: {
      fontSize: heading,
      fontWeight: 600,
    },
    variantStrongBold: {
      fontWeight: 600,
    },
    colorSupportMidnight: {
      color: colorSupportMidnight,
    },
    colorSupportSteel: {
      color: colorSupportSteel,
    },
    colorSupportSlate: {
      color: colorSupportSlate,
    },
    colorAccentTeal: {
      color: colorAccentTeal,
    },
    colorAccentAloe: {
      color: colorAccentAloe,
    },
    colorAccentWatermelon: {
      color: colorAccentWatermelon,
    },
    colorWhite: {
      color: white,
    },
    smallGutterBottom: {
      marginBottom: unit,
    },
    sectionHeader: {
      fontSize: bodyS,
      fontWeight: 600,
    },
    secondarySectionHeader: {
      fontSize: small,
      fontWeight: 600,
    },
    supportBook: {
      fontSize: small,
      fontWeight: 400,
    },
    // TODO: Update Typography to match latest Design System
    // Note that by naming variants by their values we've corrupted values for other brands
    // Eg UCSF might want to override fontSize/weight/color for some variant
    h1W600F24: {
      fontSize: heading,
      fontWeight: 600,
    },
    h2W600F20: {
      fontSize: subHeading,
      fontWeight: 600,
    },
    h2W900F20: {
      fontSize: subHeading,
      fontWeight: 900,
    },
    h3W600F17: {
      fontSize: body,
      fontWeight: 600,
    },
    h3W500F17: {
      fontSize: body,
      fontWeight: 500,
    },
    bW600F14: {
      fontSize: small,
      fontWeight: 600,
    },
    bW600F12: {
      fontSize: detail,
      fontWeight: 600,
    },
    bW400F12: {
      fontSize: detail,
      fontWeight: 400,
    },
    bW600F17: {
      fontSize: body,
      fontWeight: 600,
    },
    bW500F18: {
      fontSize: bodyG,
      fontWeight: 500,
    },
    bW600F18: {
      fontSize: bodyG,
      fontWeight: 600,
    },
    bW500F17: {
      fontSize: body,
      fontWeight: 500,
    },
    bW400F17: {
      fontSize: body,
      fontWeight: 400,
    },
    supportW400F14: {
      fontSize: small,
      fontWeight: 400,
    },
    supportW500F12: {
      fontSize: 12,
      fontWeight: 500,
    },
    supportW500F14: {
      fontSize: small,
      fontWeight: 500,
    },
    // highlights
    highlightAccentSunrise: {
      padding: `${unit / 2}px ${unit}px`,
      background: colorAccentSunrise,
      borderRadius: 4,
    },
    underline: {
      textDecoration: 'underline',
    },
  });

export interface TypographyProps
  extends Omit<MuiTypographyProps, 'classes' | 'variant' | 'color'>,
    WithStyles<typeof styles> {
  variant?:
    | MuiTypographyProps['variant']
    // variants with names matching the ones in Frontify
    // https://company-180100.frontify.com/d/xxHPHhwjYMVZ/guidelines#/brand-guidelines/brand-typography-1582749863
    | 'h1W600F24'
    | 'h2W600F20'
    | 'h2W900F20'
    | 'h3W600F17'
    | 'h3W500F17'
    | 'bW600F14'
    | 'bW400F12'
    | 'bW600F12'
    | 'bW600F17'
    | 'bW500F18'
    | 'bW600F18'
    | 'bW500F17'
    | 'bW400F17'
    | 'supportW400F14'
    | 'supportW500F12'
    | 'supportW500F14'
    // TODO unify with variants defined in Frontify
    | 'sectionHeader'
    | 'secondarySectionHeader'
    | 'customHeader'
    | 'h1SemiBold'
    | 'h6StrongBold'
    | 'body1Bold'
    | 'body1StrongBold'
    | 'body2Bold'
    | 'body2StrongBold'
    | 'captionBold'
    | 'captionStrongBold'
    | 'h4LargeStrongBold'
    | 'body1Large'
    | 'body1LargeBold'
    | 'supportBook'
    | 'body1LargeStrongBold'
    | 'pBook'
    | 'pSemiBold';
  color?:
    | MuiTypographyProps['color']
    | 'supportMidnight'
    | 'supportSteel'
    | 'supportSlate'
    | 'accentTeal'
    | 'accentAloe'
    | 'accentWatermelon'
    | 'white';
  highlight?: 'accentSunrise';
  smallGutterBottom?: boolean;
  mask?: 'number';
  to?: string;
  underline?: boolean;
  shouldOpenInANewTab?: boolean;
}

const TypographyComponent: React.FC<TypographyProps> = ({
  classes,
  className: initialClassName,
  children,
  variant: initialVariant,
  color: initialColor,
  highlight,
  smallGutterBottom,
  mask,
  to,
  underline,
  shouldOpenInANewTab,
  ...rest
}) => {
  const variant = useMemo(() => {
    if (initialVariant === 'h1SemiBold' || initialVariant === 'h1W600F24') {
      return 'h1';
    }
    if (initialVariant === 'h2W600F20' || initialVariant === 'h2W900F20') {
      return 'h2';
    }
    if (initialVariant === 'h4LargeStrongBold' || initialVariant === 'h3W600F17' || initialVariant === 'h3W500F17') {
      return 'h3';
    }
    if (initialVariant === 'customHeader' || initialVariant === 'h6StrongBold') {
      return 'h6';
    }
    if (
      initialVariant === 'body1Bold' ||
      initialVariant === 'body1StrongBold' ||
      initialVariant === 'body1Large' ||
      initialVariant === 'body1LargeBold' ||
      initialVariant === 'body1LargeStrongBold' ||
      initialVariant === 'sectionHeader' ||
      initialVariant === 'secondarySectionHeader' ||
      initialVariant === 'supportBook' ||
      initialVariant === 'pBook' ||
      initialVariant === 'pSemiBold' ||
      initialVariant === 'bW600F14' ||
      initialVariant === 'bW400F12' ||
      initialVariant === 'bW600F12' ||
      initialVariant === 'bW600F17' ||
      initialVariant === 'bW500F18' ||
      initialVariant === 'bW600F18' ||
      initialVariant === 'bW500F17' ||
      initialVariant === 'bW400F17' ||
      initialVariant === 'supportW400F14' ||
      initialVariant === 'supportW500F12' ||
      initialVariant === 'supportW500F14'
    ) {
      return 'body1';
    }
    if (initialVariant === 'body2Bold' || initialVariant === 'body2StrongBold') {
      return 'body2';
    }
    if (initialVariant === 'captionBold' || initialVariant === 'captionStrongBold') {
      return 'caption';
    }
    return initialVariant;
  }, [initialVariant]);

  const color = useMemo(() => {
    if (
      initialColor === 'supportMidnight' ||
      initialColor === 'supportSteel' ||
      initialColor === 'supportSlate' ||
      initialColor === 'accentTeal' ||
      initialColor === 'accentAloe' ||
      initialColor === 'accentWatermelon' ||
      initialColor === 'white'
    ) {
      return 'primary';
    }
    return initialColor;
  }, [initialColor]);

  const className = useMemo(
    () =>
      classNames(
        {
          [classes.variantCustomHeader]: initialVariant === 'customHeader',
          [classes.variantH4LargeStrongBold]: initialVariant === 'h4LargeStrongBold',
          [classes.variantBody1Large]:
            initialVariant === 'body1Large' ||
            initialVariant === 'body1LargeBold' ||
            initialVariant === 'body1LargeStrongBold' ||
            initialVariant === 'pBook' ||
            initialVariant === 'pSemiBold',
          [classes.variantH1Semibold]: initialVariant === 'h1SemiBold',
          [classes.variantBold]:
            initialVariant === 'body1Bold' ||
            initialVariant === 'body1LargeBold' ||
            initialVariant === 'body2Bold' ||
            initialVariant === 'captionBold',
          [classes.variantStrongBold]:
            initialVariant === 'body1StrongBold' ||
            initialVariant === 'body2StrongBold' ||
            initialVariant === 'captionStrongBold' ||
            initialVariant === 'body1LargeStrongBold' ||
            initialVariant === 'pSemiBold' ||
            initialVariant === 'h6StrongBold',
          [classes.colorSupportMidnight]: initialColor === 'supportMidnight',
          [classes.colorSupportSteel]: initialColor === 'supportSteel',
          [classes.colorSupportSlate]: initialColor === 'supportSlate',
          [classes.colorAccentTeal]: initialColor === 'accentTeal',
          [classes.colorAccentAloe]: initialColor === 'accentAloe',
          [classes.colorAccentWatermelon]: initialColor === 'accentWatermelon',
          [classes.colorWhite]: initialColor === 'white',
          [classes.smallGutterBottom]: smallGutterBottom,
          [classes.sectionHeader]: initialVariant === 'sectionHeader',
          [classes.secondarySectionHeader]: initialVariant === 'secondarySectionHeader',
          [classes.supportBook]: initialVariant === 'supportBook',
          [classes.h1W600F24]: initialVariant === 'h1W600F24',
          [classes.h2W600F20]: initialVariant === 'h2W600F20',
          [classes.h2W900F20]: initialVariant === 'h2W900F20',
          [classes.h3W600F17]: initialVariant === 'h3W600F17',
          [classes.h3W500F17]: initialVariant === 'h3W500F17',
          [classes.bW600F14]: initialVariant === 'bW600F14',
          [classes.bW400F12]: initialVariant === 'bW400F12',
          [classes.bW600F12]: initialVariant === 'bW600F12',
          [classes.bW600F17]: initialVariant === 'bW600F17',
          [classes.bW500F18]: initialVariant === 'bW500F18',
          [classes.bW600F18]: initialVariant === 'bW600F18',
          [classes.bW500F17]: initialVariant === 'bW500F17',
          [classes.bW400F17]: initialVariant === 'bW400F17',
          [classes.supportW400F14]: initialVariant === 'supportW400F14',
          [classes.supportW500F12]: initialVariant === 'supportW500F12',
          [classes.supportW500F14]: initialVariant === 'supportW500F14',
          [classes.highlightAccentSunrise]: highlight === 'accentSunrise',
          [classes.underline]: underline,
        },
        initialClassName,
      ),
    [classes, initialVariant, initialColor, highlight, smallGutterBottom, initialClassName, underline],
  );

  const text = useMemo(() => {
    if (mask === 'number' && (isString(children) || isNumber(children))) {
      return `${children}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return children;
  }, [children, mask]);

  const linkProps = shouldOpenInANewTab ? { target: 'blank', rel: 'noopener noreferrer' } : {};

  return (
    <MuiTypography className={className} variant={variant} color={color} {...rest}>
      {to ? (
        <Link to={to} {...linkProps}>
          {text}
        </Link>
      ) : (
        text
      )}
    </MuiTypography>
  );
};

export const Typography = withStyles(styles)(TypographyComponent);
